import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import useRozmer from '../../hooks/useRozmer';
import { MODAL_SKLO } from '../../lib/uiTokens';
import SheetModal, { SheetKarta, SheetNadpisSkupiny } from '../../components/SheetModal';
import { ArrowLeftRight, CalendarDays, CalendarOff, ChevronLeft, ChevronRight, Clock, Crown, Phone, RotateCcw, Unlock, Users, X } from 'lucide-react';
import VratitSmenuModal from '../../components/VratitSmenuModal';
import ProhoditSmenuModal from '../../components/ProhoditSmenuModal';
import ZadostOVolnoSheet from './ZadostOVolnoSheet';
import VolneSmenySheet from './VolneSmenySheet';
import VolnyFlek from './VolnyFlek';

const MESICE_CZ = [
    'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
    'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec',
];
const MESICE_ZKRATKA = ['Led', 'Únor', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'];
const DNY_KRATKE = ['po', 'út', 'st', 'čt', 'pá', 'so', 'ne'];

const MEZERA = 4; // rozestup buněk mřížky (px) — potřeba i pro dopočet výšky buňky
const sevri = (min, v, max) => Math.max(min, Math.min(max, v));

const AVATAR_GRADIENTS = [
    'linear-gradient(135deg, #1D9E75, #10B981)',
    'linear-gradient(135deg, #6C4192, #975ACD)',
    'linear-gradient(135deg, #F59E0B, #FF8A00)',
    'linear-gradient(135deg, #3B82F6, #06B6D4)',
    'linear-gradient(135deg, #EC4899, #A855F7)',
    'linear-gradient(135deg, #14B8A6, #0CC0DF)',
];
const avatarGradient = (id) => AVATAR_GRADIENTS[Math.abs(Number(id) || 0) % AVATAR_GRADIENTS.length];
const initials = (j, p) => `${j?.[0] ?? ''}${p?.[0] ?? ''}`.toUpperCase();

const fmtKc = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');
const fmtH = (n) => (n == null ? '—' : `${Number(n).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} h`);
const fmtKcSigned = (n) => {
    if (n == null) return '—';
    const v = Math.round(Number(n));
    if (v === 0) return '0 Kč';
    return (v > 0 ? '+' : '−') + Math.abs(v).toLocaleString('cs-CZ') + ' Kč';
};
const isoDatum = (r, m, d) => `${r}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

// Volný flek se hlásí akcentem cockpitu (oranžová patří dnešku, barvy šablon
// mým směnám) — tečkovaně, aby se nepletl s cizím dnem.
const BARVA_VOLNE = '#0CC0DF';

const TYP_NEPRITOMNOSTI = {
    dovolena: { label: 'Dovolená', barva: '#3B82F6' },
    nemoc:    { label: 'Nemoc',    barva: '#EF4444' },
    osobni:   { label: 'Osobní',   barva: '#F59E0B' },
    jine:     { label: 'Jiné',     barva: '#6B7280' },
};
const STAV_NEPRITOMNOSTI = {
    ceka:      { label: 'Čeká na schválení', barva: '#F59E0B' },
    schvaleno: { label: 'Schváleno',         barva: '#10B981' },
    zamitnuto: { label: 'Zamítnuto',         barva: '#EF4444' },
};

// Stejné prahy, jaké vynucuje backend i modul Směny: vrátit jde do 48 h před
// začátkem, prohodit do 12 h. Opsat je sem je levnější než další endpoint,
// ale musí zůstat na jednom místě — ne u každého tlačítka zvlášť.
const hodinDoZacatku = (datum, casOd) =>
    (new Date(`${datum}T${(casOd || '00:00')}:00`) - Date.now()) / 3600000;
const lzeVratit = (datum, s) => s.stav !== 'dokoncena' && hodinDoZacatku(datum, s.cas_od) >= 48;
const lzeProhodit = (datum, s) => s.stav !== 'dokoncena' && hodinDoZacatku(datum, s.cas_od) >= 12;

/** „dnes" / „zítra" / „po 14. 9." — relativně k dnešku. */
function popisDne(datum, dnesObj) {
    const den = new Date(datum + 'T00:00:00');
    const dnes = new Date(dnesObj.getFullYear(), dnesObj.getMonth(), dnesObj.getDate());
    const rozdil = Math.round((den - dnes) / 86400000);
    if (rozdil === 0) return 'dnes';
    if (rozdil === 1) return 'zítra';
    return den.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
}

/** Kolečko s avatarem (fallback iniciály na barevném gradientu). */
function Avatar({ clen, size = 18, ring }) {
    return (
        <span style={{
            position: 'relative', overflow: 'hidden', flexShrink: 0,
            width: size, height: size, borderRadius: '50%',
            background: avatarGradient(clen.zamestnanec_id),
            color: '#fff',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: Math.round(size * 0.42), fontWeight: 800, lineHeight: 1,
            ...(ring ? { boxShadow: `0 0 0 1.5px ${ring}` } : null),
        }}>
            <span>{initials(clen.jmeno, clen.prijmeni)}</span>
            {clen.avatar_url && (
                <img src={clen.avatar_url} alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'cover', objectPosition: clen.avatar_position || 'center',
                    }} />
            )}
        </span>
    );
}

/**
 * Desktopový kalendář mých směn — levý blok horní řady cockpitu.
 * Data ze stejného endpointu jako mobilní `MojeSmena/MonthOverview`
 * (`/api/me/mesic`), jen bohatší: v buňce dne je čas směny a avatary kolegů,
 * po kliknutí se otevře detail dne (kdo je se mnou, kontakt, skutečnost).
 * Dole souhrn aktuálního výdělku vůči plánu měsíce.
 *
 * `mobil` — sekce „Můj měsíc" v Přehledu na telefonu: nahoře velký měsíc
 * s dotykovými šipkami, měsíce jdou listovat i přejetím po mřížce, výdělek
 * vždy kompaktně (místo patří dnům).
 */
export default function KalendarSmen({ isDark, cardStyle, kurzor: kurzorZvenku, onKurzor, mobil = false, datum = null }) {
    const dnesObj = useMemo(() => new Date(), []);
    // Rok+měsíc držíme v jednom stavu — dva oddělené settery by se při rychlém
    // proklikávání mohly rozejít (přelom roku). Když měsíc drží rodič (Přehled
    // podle něj filtruje KPI), kalendář je řízený; jinak si ho drží sám.
    const [kurzorVlastni, setKurzorVlastni] = useState({ rok: dnesObj.getFullYear(), mesic: dnesObj.getMonth() + 1 });
    const kurzor = kurzorZvenku ?? kurzorVlastni;
    const setKurzor = onKurzor ?? setKurzorVlastni;
    const { rok, mesic } = kurzor;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null); // { den, rect }
    // Příští směna nezávisí na zobrazeném měsíci — drží se zvlášť, ať při
    // listování měsíci nezmizí z hlavičky.
    const [pristi, setPristi] = useState(undefined);
    const [kartaRef, karta] = useRozmer();
    const [mrizkaRef, mrizka] = useRozmer();

    // Akce (vzít flek, vrátit, prohodit, požádat o volno) nemění měsíc, ale musí
    // překreslit data — proto zvlášť od `rok`/`mesic`.
    const [verze, setVerze] = useState(0);
    const obnov = useCallback(() => setVerze(v => v + 1), []);

    const [zadost, setZadost] = useState(null);      // { datum } — sheet žádosti o volno
    const [seznamVolnych, setSeznamVolnych] = useState(false);
    const [vratitSmenu, setVratitSmenu] = useState(null);
    const [prohoditSmenu, setProhoditSmenu] = useState(null);
    const [beruId, setBeruId] = useState(null);      // id fleku, který se právě bere
    const [chybaAkce, setChybaAkce] = useState(null);

    // Vrátit / prohodit se otevírá z detailu dne — ten musí nejdřív zmizet.
    // Detail jede na vlastním portálu (mobil sheet, desktop bublina u buňky),
    // a nechat ho pod modalem znamená dvě plátna přes sebe: na desktopu je
    // bublina dokonce výš, takže modal je pod ní a není vidět vůbec.
    const otevriVratit = useCallback((s) => { setDetail(null); setVratitSmenu(s); }, []);
    const otevriProhodit = useCallback((s) => { setDetail(null); setProhoditSmenu(s); }, []);

    useEffect(() => {
        let zrusen = false;
        setLoading(true);
        setDetail(null);
        fetch(`/api/me/mesic?rok=${rok}&mesic=${mesic}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (zrusen) return;
                setData(d);
                if (d && 'pristi_smena' in d) setPristi(d.pristi_smena);
            })
            .finally(() => { if (!zrusen) setLoading(false); });
        return () => { zrusen = true; };
    }, [rok, mesic, verze]);

    // Převzetí volného fleku. Backend kontroluje kapacitu, duplicitu, konflikt
    // i kvalifikaci znovu — tlačítko se nabízí jen tam, kde `muzu` prošlo, ale
    // mezitím si flek mohl vzít někdo jiný, takže se chyba musí umět ukázat.
    const vzitFlek = useCallback(async (flek) => {
        setBeruId(flek.id);
        setChybaAkce(null);
        try {
            const res = await fetch(`/api/shifts/${flek.id}/prevzit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ zamestnanec_id: data?.zamestnanec_id }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d?.error || `Chyba ${res.status}`);
            }
            obnov();
        } catch (e) {
            setChybaAkce(e.message || 'Směnu se nepodařilo převzít.');
        } finally {
            setBeruId(null);
        }
    }, [data?.zamestnanec_id, obnov]);

    // Proklik z notifikace nese datum — kalendář na něj přepne měsíc a níž
    // (až dorazí data) otevře detail toho dne. Bez toho by člověk skončil
    // v aktuálním měsíci a směnu si musel najít sám.
    useEffect(() => {
        if (!datum) return;
        const [r, m] = datum.split('-').map(Number);
        if (r && m) setKurzor({ rok: r, mesic: m });
    }, [datum]);

    const posunMesic = useCallback((krok) => {
        setKurzor(k => {
            const idx = (k.rok * 12 + (k.mesic - 1)) + krok;
            return { rok: Math.floor(idx / 12), mesic: (idx % 12) + 1 };
        });
    }, []);

    const dnyKalendare = useMemo(() => {
        const prvni = new Date(rok, mesic - 1, 1);
        const posledni = new Date(rok, mesic, 0);
        const offsetPondeli = (prvni.getDay() + 6) % 7; // pondělí jako první den
        const buffer = [];
        for (let i = 0; i < offsetPondeli; i++) buffer.push(null);
        for (let d = 1; d <= posledni.getDate(); d++) buffer.push({ den: d, datum: isoDatum(rok, mesic, d) });
        while (buffer.length % 7 !== 0) buffer.push(null);
        return buffer;
    }, [rok, mesic]);

    // Kolik týdnů měsíc reálně zabírá (4–6). Pevných 6 řádků by u kratšího
    // měsíce nechalo dole prázdný pás — takhle mřížka vyplní dlaždici vždy.
    const radky = dnyKalendare.length / 7;

    const dnyMap = useMemo(() => new Map((data?.dny || []).map(d => [d.datum, d])), [data]);

    // Detail se otevře jen jednou za proklik — jinak by ho každý refresh
    // (převzetí fleku, žádost o volno) otevřel znovu pod rukama.
    const otevrenoZProkliku = useRef(null);
    useEffect(() => {
        if (!datum || loading || otevrenoZProkliku.current === datum) return;
        const den = (data?.dny || []).find(d => d.datum === datum);
        if (!den) return;
        otevrenoZProkliku.current = datum;
        setDetail({ den, rect: mrizkaRef.current?.getBoundingClientRect?.() ?? { left: 0, bottom: 0, top: 0, width: 0 } });
    }, [datum, loading, data]);

    const dnesDatum = isoDatum(dnesObj.getFullYear(), dnesObj.getMonth() + 1, dnesObj.getDate());
    const jeAktualniMesic = rok === dnesObj.getFullYear() && mesic === dnesObj.getMonth() + 1;

    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const dayBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';

    const aktualniKc = data?.vydelek?.aktualni_kc ?? 0;
    const planovanyKc = data?.vydelek?.planovany_kc ?? 0;
    const dyskoKc = data?.vydelek?.dysko_kc ?? 0;
    const procento = planovanyKc > 0 ? Math.min(100, Math.round((aktualniKc / planovanyKc) * 100)) : 0;
    const hodAkt = data?.hodiny?.aktualni ?? 0;
    const hodPlan = data?.hodiny?.planovane ?? 0;
    const pocetSmen = (data?.dny || []).reduce((s, d) => s + (d.smeny?.length || 0), 0);
    const volnePocet = data?.volne_pocet ?? 0;
    // V měsíci už nic nezbývá, ale dál ano — chip musí zůstat, jinak se na seznam
    // nedá doklepat a člověk myslí, že není co vzít.
    const volnePozdeji = data?.volne_pozdeji ?? 0;

    // Směny bez hodinové sazby (sazba 0 — typicky vedení) mají výdělek 0 Kč,
    // přestože směny jsou. Panel pak neukazuje Kč, ale odpracované hodiny vůči
    // plánu — jinak by 0 / 0 Kč vypadalo jako „žádná směna".
    const bezSazby = pocetSmen > 0 && planovanyKc === 0 && aktualniKc === 0;
    const procentoHodin = hodPlan > 0 ? Math.min(100, Math.round((hodAkt / hodPlan) * 100)) : 0;
    const panelHodnota = bezSazby
        ? Number(hodAkt).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })
        : Math.round(aktualniKc).toLocaleString('cs-CZ');
    const panelCil = bezSazby ? fmtH(hodPlan) : fmtKc(planovanyKc);
    const panelProcento = bezSazby ? procentoHodin : procento;

    // ── Adaptivní metriky ──
    // Vše se počítá z reálné velikosti buňky. Dokud není změřeno (první
    // render), jedou výchozí hodnoty, aby to neprobliklo prázdnou mřížkou.
    const bunkaH = mrizka.h ? (mrizka.h - MEZERA * (radky - 1)) / radky : 0;
    const bunkaW = mrizka.w ? (mrizka.w - MEZERA * 6) / 7 : 0;
    const zmereno = bunkaH > 0 && bunkaW > 0;

    // Pod ~34 px se do buňky nic kromě čísla nevejde — degradujeme na mobilní
    // podobu (číslo na střed + barevná tečka).
    const mini = zmereno && bunkaH < 34;
    // Těsná buňka (typicky FullHD, kde na řádek vyjde ~46 px) ubere odsazení
    // i rozestupy, ať se čas i avatary pořád vejdou.
    const tesno = zmereno && bunkaH < 54;
    const bunkaPad = mini ? 2 : (tesno ? '3px 4px' : '4px 5px');
    const bunkaGap = tesno ? 1 : 2;

    const cisloFont = zmereno ? sevri(9.5, bunkaH * 0.26, 15) : 11;
    const zobrazCas = !zmereno || (!mini && bunkaW >= 38);
    const plnyCas = !zmereno || bunkaW >= 68;
    const casFont = zmereno ? Math.min(sevri(8.5, bunkaW * 0.145, 11.5), bunkaH * 0.24) : 9.5;
    const avatarSize = zmereno
        ? Math.round(sevri(12, Math.min(bunkaW * 0.3, bunkaH * 0.28), 20))
        : 15;
    const zobrazAvatary = !zmereno || (bunkaH >= 44 && bunkaW >= 34);
    const maxAvataru = zmereno
        ? Math.round(sevri(1, Math.floor((bunkaW - 14 - avatarSize) / Math.max(6, avatarSize - 5)) + 1, 4))
        : 3;

    // Výška celé karty řídí, kolik místa si vezme hlavička a panel výdělku.
    // Rozměr karty je vnější (včetně odsazení) — prahy o odsazení vyšší než dřív.
    const kompakt = mobil || (karta.h > 0 && karta.h < 410);
    const uzky = !mobil && karta.w > 0 && karta.w < 360;

    // Volné fleky a žádost o volno. Na mobilu jsou pod hlavičkou v plné šířce,
    // na desktopu v prázdném místě uprostřed řádku — tam nestojí ani pixel
    // výšky, kterou by jinak musel ubrat kalendář.
    const akceKalendare = (drobne = false, bezPopisku = false) => {
        const vyska = drobne ? 24 : 'auto';
        const zaklad = {
            display: 'inline-flex', alignItems: 'center', gap: drobne ? 4 : 5,
            height: vyska, padding: drobne ? '0 9px' : '5px 11px',
            borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
            fontSize: drobne ? 11 : 12, flexShrink: 0,
        };
        return (
            <>
                {(volnePocet > 0 || volnePozdeji > 0) && (
                    <button type="button" onClick={() => setSeznamVolnych(true)}
                        title={volnePocet > 0
                            ? `${volnePocet} ${sklonujVolne(volnePocet)} k rozebrání`
                            : `V tomhle měsíci nic, později ${volnePozdeji} ${sklonujVolne(volnePozdeji)}`}
                        style={{
                            ...zaklad, fontWeight: volnePocet > 0 ? 700 : 600,
                            background: volnePocet > 0 ? 'rgba(12,192,223,0.12)' : 'transparent',
                            color: volnePocet > 0 ? BARVA_VOLNE : 'var(--text-tertiary)',
                            border: `1px ${volnePocet > 0 ? 'solid rgba(12,192,223,0.35)' : 'dashed var(--border-default)'}`,
                        }}>
                        <Unlock size={drobne ? 11 : 12} />
                        {volnePocet > 0
                            ? (bezPopisku ? volnePocet
                                : drobne ? `${volnePocet} ${sklonujVolne(volnePocet)}`
                                : `${volnePocet} ${sklonujVolne(volnePocet)} k rozebrání`)
                            : (bezPopisku ? volnePozdeji
                                : drobne ? `${volnePozdeji} později`
                                : `Volné až později · ${volnePozdeji}`)}
                    </button>
                )}
                <button type="button" onClick={() => setZadost({ datum: null })}
                    title="Požádat o volno"
                    style={{
                        ...zaklad, fontWeight: 600,
                        background: 'transparent', color: labelMuted,
                        border: `1px dashed ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'}`,
                    }}>
                    <CalendarOff size={drobne ? 11 : 12} />
                    {bezPopisku ? 'Volno' : 'Požádat o volno'}
                </button>
            </>
        );
    };

    const navBtn = mobil ? {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 34, height: 34, borderRadius: 10,
        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.55)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.85)'}`,
        color: textPrimary, cursor: 'pointer', padding: 0, flexShrink: 0,
    } : {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 24, height: 24, borderRadius: 6,
        background: 'none', border: `1px solid ${hranice}`,
        color: labelMuted, cursor: 'pointer', padding: 0,
    };

    // Mobil: přejetí po mřížce doleva/doprava = další/předchozí měsíc
    const tah = useRef(null);
    const tahProps = mobil ? {
        onTouchStart: (e) => { const t = e.touches[0]; tah.current = { x: t.clientX, y: t.clientY }; },
        onTouchEnd: (e) => {
            const z = tah.current;
            tah.current = null;
            if (!z) return;
            const t = e.changedTouches[0];
            const dx = t.clientX - z.x;
            const dy = t.clientY - z.y;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) posunMesic(dx < 0 ? 1 : -1);
        },
    } : null;

    // Příští směna je nejčastější důvod, proč se sem člověk dívá — klik = detail dne
    const pristiRadek = pristi && (
        <button type="button"
            onClick={(e) => setDetail({
                den: {
                    datum: pristi.datum,
                    smeny: [{ id: pristi.id, sablon_nazev: pristi.sablon_nazev, sablon_barva: pristi.sablon_barva, cas_od: pristi.cas_od, cas_do: pristi.cas_do, hodin: pristi.hodin, stav: pristi.stav }],
                    kolegove: pristi.kolegove || [],
                    skutecnost: null,
                },
                rect: e.currentTarget.getBoundingClientRect(),
            })}
            title={(pristi.kolegove || []).map(k => `${k.jmeno} ${k.prijmeni}`).join(', ') || undefined}
            style={{
                display: 'block', maxWidth: '100%', padding: 0, border: 'none', background: 'none',
                cursor: 'pointer', textAlign: 'left',
                fontSize: mobil ? 12.5 : 11, color: textMuted, fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
            <span style={{ color: '#0CC0DF', fontWeight: 600 }}>Příští </span>
            <b style={{ color: textPrimary, fontWeight: 600 }}>{popisDne(pristi.datum, dnesObj)}</b>
            {' '}{pristi.cas_od}–{pristi.cas_do}
            {(pristi.kolegove || []).length > 0 && (
                <> · {pristi.kolegove.slice(0, 2).map(k => k.jmeno).join(', ')}
                    {pristi.kolegove.length > 2 && ` +${pristi.kolegove.length - 2}`}</>
            )}
        </button>
    );
    const pocetSmenText = loading
        ? 'Načítám…'
        : `${pocetSmen} ${pocetSmen === 1 ? 'směna' : (pocetSmen >= 2 && pocetSmen <= 4 ? 'směny' : 'směn')} v měsíci`;

    return (
        <div ref={kartaRef} className="rounded-xl" style={{
            ...cardStyle, height: '100%',
            padding: kompakt ? 12 : 16,
            display: 'flex', flexDirection: 'column', gap: kompakt ? 7 : 10,
            minHeight: 0, overflow: 'hidden',
        }}>
            {/* Mobil — velký měsíc a dotykové šipky; pod tím příští směna
                (u jiného měsíce počet směn v něm). */}
            {mobil && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalendarDays size={18} style={{ color: '#0CC0DF', flexShrink: 0 }} />
                        <span style={{
                            flex: 1, minWidth: 0, fontSize: 18, fontWeight: 750, letterSpacing: -0.2,
                            color: textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{MESICE_CZ[mesic - 1]} {rok}</span>
                        {!jeAktualniMesic && (
                            <button type="button" onClick={() => setKurzor({ rok: dnesObj.getFullYear(), mesic: dnesObj.getMonth() + 1 })}
                                style={{ ...navBtn, width: 'auto', padding: '0 12px', fontSize: 13, fontWeight: 600 }}>
                                Dnes
                            </button>
                        )}
                        <button type="button" onClick={() => posunMesic(-1)} aria-label="Předchozí měsíc" style={navBtn}>
                            <ChevronLeft size={18} />
                        </button>
                        <button type="button" onClick={() => posunMesic(1)} aria-label="Další měsíc" style={navBtn}>
                            <ChevronRight size={18} />
                        </button>
                    </div>
                    <div style={{ paddingLeft: 26, minWidth: 0 }}>
                        {jeAktualniMesic && pristiRadek
                            ? pristiRadek
                            : <div style={{ fontSize: 12.5, color: textMuted }}>{pocetSmenText}</div>}
                    </div>

                    {/* Co si můžu vzít a jak si říct o volno — dvě akce, které
                        v Mém měsíci dávaly smysl odjakživa, jen tu nikdy nebyly. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 26, flexWrap: 'wrap' }}>
                        {akceKalendare()}
                    </div>
                </div>
            )}

            {/* Hlavička — název + příští směna (klik = detail dne) + přepínač
                měsíce. Bez příští směny podtitulek nese počet směn v měsíci. */}
            {!mobil && (
            <div style={{ display: 'flex', alignItems: 'center', gap: uzky ? 5 : 8, flexShrink: 0 }}>
                <CalendarDays size={16} style={{ color: '#0CC0DF', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary, lineHeight: 1.2 }}>
                        Moje směny
                    </div>
                    {/* Příští směna zůstává i na nízké dlaždici (počet směn v měsíci ne). */}
                    {pristi ? pristiRadek : !kompakt && (
                        <div style={{ fontSize: 11, color: textMuted }}>{pocetSmenText}</div>
                    )}
                </div>
                {/* Na úzké dlaždici se akce nevejdou vedle přepínače měsíce —
                    tam zůstávají dostupné z detailu dne (Požádat o volno)
                    a z buněk s tečkovaným obrysem (volné fleky). */}
                {!uzky && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {/* Na širší dlaždici je uprostřed hlavičky místa dost na
                            celé popisky; holý počet u zámku se čte jako číslo dne. */}
                        {akceKalendare(true, karta.w > 0 && karta.w < 620)}
                    </div>
                )}
                {!jeAktualniMesic && !uzky && (
                    <button type="button" onClick={() => setKurzor({ rok: dnesObj.getFullYear(), mesic: dnesObj.getMonth() + 1 })}
                        style={{ ...navBtn, width: 'auto', padding: '0 8px', fontSize: 11, fontWeight: 600 }}>
                        Dnes
                    </button>
                )}
                <button type="button" onClick={() => posunMesic(-1)} aria-label="Předchozí měsíc" style={navBtn}>
                    <ChevronLeft size={14} />
                </button>
                <span title={`${MESICE_CZ[mesic - 1]} ${rok}`} style={{
                    fontSize: 12, fontWeight: 600, color: textPrimary,
                    minWidth: uzky ? 62 : 96, textAlign: 'center', whiteSpace: 'nowrap',
                }}>{(uzky ? MESICE_ZKRATKA : MESICE_CZ)[mesic - 1]} {rok}</span>
                <button type="button" onClick={() => posunMesic(1)} aria-label="Další měsíc" style={navBtn}>
                    <ChevronRight size={14} />
                </button>
            </div>
            )}

            {/* Záhlaví dnů */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: MEZERA,
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                color: textMuted, textAlign: 'center', flexShrink: 0,
            }}>
                {DNY_KRATKE.map(d => <span key={d}>{d}</span>)}
            </div>

            {/* Mřížka dnů — vyplní zbytek karty. Řádky `minmax(0, 1fr)` podle
                reálného počtu týdnů, nikdy fixní výška buňky: obsah se
                přizpůsobuje mřížce, ne naopak. */}
            <div ref={mrizkaRef} {...tahProps} style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                gridTemplateRows: `repeat(${radky}, minmax(0, 1fr))`, gap: MEZERA,
                flex: 1, minHeight: 0,
            }}>
                {dnyKalendare.map((b, i) => {
                    if (!b) return <span key={`e-${i}`} />;
                    const zaznam = dnyMap.get(b.datum);
                    const smeny = zaznam?.smeny || [];
                    const kolegove = zaznam?.kolegove || [];
                    const cizi = zaznam?.cizi || [];
                    const volne = zaznam?.volne || [];
                    const nedostupnost = zaznam?.nedostupnost || null;

                    // Pravidlo buňky: **barva = moje**. Den, kde jede někdo jiný,
                    // je černobílý — ale živý, dá se rozkliknout. Volný flek musí
                    // mít znak navíc (tečkovaný obrys), jinak splyne s cizím dnem
                    // a zmizí jediná věc na dni, se kterou jde něco udělat.
                    const maSmenu = smeny.length > 0;
                    const volneProMe = volne.filter(v => v.muzu);
                    const maVolne = volneProMe.length > 0;
                    const maCizi = cizi.length > 0;
                    const maObsah = maSmenu || maCizi || volne.length > 0 || !!nedostupnost;
                    const lide = maSmenu ? kolegove : cizi;

                    const isToday = b.datum === dnesDatum;
                    const isPast = b.datum < dnesDatum;
                    const barva = smeny[0]?.sablon_barva || '#0CC0DF';
                    const jeVikend = i % 7 >= 5;
                    const typNepr = nedostupnost ? (TYP_NEPRITOMNOSTI[nedostupnost.typ] || TYP_NEPRITOMNOSTI.jine) : null;

                    // Rámeček nese právě jeden stav, v pořadí moje > nepřítomnost > volné > cizí.
                    const ramecek = isToday ? '1px solid rgba(255,138,0,0.55)'
                        : maSmenu ? `1px solid ${hranice}`
                        : nedostupnost ? `1px dashed ${typNepr.barva}66`
                        : maVolne ? `1px dashed ${BARVA_VOLNE}88`
                        : '1px solid transparent';
                    // Cizí den zůstává bez výplně — šedá na šedém pozadí zmizí,
                    // takže ho nese obrys a odbarvené avatary.
                    const pozadi = isToday ? 'rgba(255,138,0,0.14)'
                        : maSmenu ? dayBg
                        : maVolne ? (isDark ? 'rgba(12,192,223,0.08)' : 'rgba(12,192,223,0.06)')
                        : 'transparent';
                    const tecka = maSmenu ? barva
                        : nedostupnost ? typNepr.barva
                        : maVolne ? BARVA_VOLNE
                        : maCizi ? (isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)')
                        : null;
                    const titulek = maSmenu
                        ? smeny.map(s => `${s.cas_od}–${s.cas_do} ${s.sablon_nazev || ''}`.trim()).join(', ')
                        : nedostupnost ? `${typNepr.label} — ${(STAV_NEPRITOMNOSTI[nedostupnost.stav] || {}).label || nedostupnost.stav}`
                        : maVolne ? `${volneProMe.length}× volná směna k rozebrání`
                        : maCizi ? `Jede: ${cizi.map(k => `${k.jmeno} ${k.prijmeni}`).join(', ')}`
                        : undefined;

                    return (
                        <button key={b.datum} type="button"
                            onClick={(e) => maObsah && setDetail({
                                den: zaznam,
                                rect: e.currentTarget.getBoundingClientRect(),
                            })}
                            disabled={!maObsah}
                            title={titulek}
                            style={{
                                position: 'relative', textAlign: mini ? 'center' : 'left',
                                display: 'flex', flexDirection: 'column',
                                alignItems: mini ? 'center' : 'stretch',
                                justifyContent: mini ? 'center' : 'flex-start',
                                gap: bunkaGap,
                                padding: bunkaPad,
                                borderRadius: mini ? 6 : 8,
                                background: pozadi,
                                border: ramecek,
                                borderLeft: maSmenu && !isToday && !mini ? `2px solid ${barva}` : undefined,
                                cursor: maObsah ? 'pointer' : 'default',
                                opacity: isPast && !maSmenu ? 0.45 : 1,
                                overflow: 'hidden',
                                transition: 'background 0.15s ease',
                            }}>
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                                <span style={{
                                    fontSize: cisloFont, fontWeight: isToday || maSmenu ? 700 : 500,
                                    fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                                    color: isToday ? '#FF8A00'
                                        : maSmenu ? textPrimary
                                        : maVolne ? BARVA_VOLNE
                                        : (maCizi || nedostupnost) ? labelMuted
                                        : (jeVikend ? textMuted : textPrimary),
                                }}>{b.den}</span>
                                {/* Nevejdou-li se avatary, alespoň počet kolegů — v řádku s číslem
                                    dne, aby nebral výšku, kterou potřebuje čas směny. */}
                                {!mini && lide.length > 0 && !zobrazAvatary && (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
                                        fontSize: Math.max(8, casFont - 0.5), fontWeight: 700, color: textMuted,
                                        fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                                    }}>
                                        <Users size={Math.max(8, casFont - 1)} />{lide.length}
                                    </span>
                                )}
                            </span>

                            {/* Mini režim — na čas ani avatary není místo, zůstává tečka
                                (stejný jazyk jako mobilní měsíční přehled). */}
                            {mini && tecka && (
                                <span style={{
                                    position: 'absolute', bottom: 2,
                                    width: 4, height: 4, borderRadius: '50%',
                                    background: maSmenu || nedostupnost || maVolne ? tecka : 'transparent',
                                    border: maSmenu || nedostupnost || maVolne ? 'none' : `1px solid ${tecka}`,
                                    boxShadow: maSmenu && kolegove.length > 0
                                        ? `5px 0 0 ${isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)'}` : 'none',
                                }} />
                            )}

                            {maSmenu && !mini && zobrazCas && (
                                <span style={{
                                    fontSize: casFont, lineHeight: 1.1, color: labelMuted,
                                    fontVariantNumeric: 'tabular-nums',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                    {plnyCas
                                        ? `${smeny[0].cas_od}–${smeny[smeny.length - 1].cas_do}`
                                        : smeny[0].cas_od}
                                </span>
                            )}

                            {/* Volný flek — na dni bez mojí směny stojí čas za
                                nabídku, ne za informaci; proto s ikonou. */}
                            {!maSmenu && maVolne && !mini && zobrazCas && (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    fontSize: casFont, lineHeight: 1.1, color: BARVA_VOLNE, fontWeight: 700,
                                    fontVariantNumeric: 'tabular-nums',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                    <Unlock size={Math.max(8, casFont - 1)} style={{ flexShrink: 0 }} />
                                    {plnyCas ? volneProMe[0].cas_od : (volneProMe.length > 1 ? volneProMe.length : '')}
                                </span>
                            )}

                            {/* Nepřítomnost — vlastní, takže barevná; čekající je
                                světlejší než schválená, ať je rozdíl vidět bez kliknutí. */}
                            {!maSmenu && nedostupnost && !mini && zobrazCas && (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    fontSize: casFont, lineHeight: 1.1, fontWeight: 700,
                                    color: typNepr.barva, opacity: nedostupnost.stav === 'ceka' ? 0.75 : 1,
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                    <CalendarOff size={Math.max(8, casFont - 1)} style={{ flexShrink: 0 }} />
                                    {plnyCas ? typNepr.label : ''}
                                </span>
                            )}

                            {/* Kdo je se mnou — avatarů se vejde tolik, kolik unese šířka buňky */}
                            {/* Kdo tam je. Na mém dni jsou to kolegové v barvě,
                                na cizím dni odbarvení — „barva = moje" platí i na avatarech. */}
                            {!mini && lide.length > 0 && zobrazAvatary && (
                                <span style={{
                                    display: 'flex', alignItems: 'center', marginTop: 'auto', minWidth: 0,
                                    ...(maSmenu ? null : { filter: 'grayscale(1)', opacity: 0.7 }),
                                }}>
                                    {lide.slice(0, maxAvataru).map((k, idx) => (
                                        <span key={k.zamestnanec_id ?? idx} style={{ marginLeft: idx ? -5 : 0, display: 'inline-flex' }}>
                                            <Avatar clen={k} size={avatarSize} ring={isDark ? '#1a1a1f' : '#fff'} />
                                        </span>
                                    ))}
                                    {lide.length > maxAvataru && (
                                        <span style={{
                                            marginLeft: 3, fontSize: Math.max(8, avatarSize * 0.55), fontWeight: 700,
                                            color: textMuted, fontVariantNumeric: 'tabular-nums',
                                        }}>+{lide.length - maxAvataru}</span>
                                    )}
                                </span>
                            )}

                        </button>
                    );
                })}
            </div>

            {/* Výdělek — stejná logika jako mobilní přehled: velké číslo vs cíl,
                progress bar, dole odpracované hodiny + procento. Na nízké
                dlaždici se panel scvrkne na jeden řádek + bar, aby zbylo místo
                na mřížku — kalendář je tu hlavní, výdělek doprovod. */}
            <div style={{
                paddingTop: kompakt ? 8 : 12, marginTop: 2, borderTop: `1px solid ${hranice}`,
                display: 'flex', flexDirection: 'column', gap: kompakt ? 5 : 8, flexShrink: 0,
            }}>
                {!loading && pocetSmen === 0 ? (
                    <span style={{ fontSize: 11.5, color: textMuted }}>
                        V tomto měsíci nemáš žádnou směnu.
                    </span>
                ) : kompakt ? (
                    <>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{
                                fontSize: 12, color: labelMuted, fontVariantNumeric: 'tabular-nums',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {bezSazby && 'Odpracováno '}
                                <strong style={{ fontSize: 16, fontWeight: 800, color: textPrimary }}>
                                    {panelHodnota}
                                </strong>
                                {' / '}{panelCil}
                            </span>
                            <span style={{
                                fontSize: 12, fontWeight: 800, color: '#FF8A00',
                                fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                            }}>{panelProcento} %</span>
                        </div>

                        <div style={{
                            height: 5, borderRadius: 3,
                            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                height: '100%', width: `${panelProcento}%`,
                                background: 'linear-gradient(90deg, #FF8A00, #F59E0B)',
                                borderRadius: 3, transition: 'width 0.3s ease',
                            }} />
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{
                                fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5,
                                textTransform: 'uppercase', color: labelMuted,
                            }}>
                                {bezSazby ? 'Odpracováno' : 'Vyděláno'}{jeAktualniMesic ? ' tento měsíc' : ''}
                            </span>
                            {dyskoKc !== 0 && (
                                <span style={{
                                    fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                    color: dyskoKc >= 0 ? '#10B981' : '#EF4444',
                                }}>{fmtKcSigned(dyskoKc)} dýško</span>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                            <strong style={{
                                fontSize: 26, fontWeight: 800, color: textPrimary,
                                fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5, lineHeight: 1,
                            }}>{panelHodnota}{bezSazby ? ' h' : ''}</strong>
                            <span style={{ fontSize: 12, color: textMuted, fontVariantNumeric: 'tabular-nums' }}>
                                / {panelCil} {bezSazby ? 'plán' : 'cíl'}
                            </span>
                        </div>

                        <div style={{
                            height: 7, borderRadius: 3,
                            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                height: '100%', width: `${panelProcento}%`,
                                background: 'linear-gradient(90deg, #FF8A00, #F59E0B)',
                                borderRadius: 3, transition: 'width 0.3s ease',
                            }} />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                fontSize: 11, color: labelMuted, fontVariantNumeric: 'tabular-nums',
                            }}>
                                <Clock size={11} style={{ flexShrink: 0 }} />
                                {bezSazby
                                    ? 'Směny nemají hodinovou sazbu — výdělek se nepočítá'
                                    : `${Number(hodAkt).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} / ${fmtH(hodPlan)} odpracováno`}
                            </span>
                            <span style={{
                                fontSize: 12, fontWeight: 800, color: '#FF8A00',
                                fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                            }}>{panelProcento} %</span>
                        </div>
                    </>
                )}
            </div>

            {detail && (mobil
                ? <DetailDneSheet den={detail.den} onClose={() => setDetail(null)} isDark={isDark}
                    onVzit={vzitFlek} beruId={beruId} chybaAkce={chybaAkce}
                    onVratit={otevriVratit} onProhodit={otevriProhodit}
                    onZadost={(d) => setZadost({ datum: d })} />
                : <DetailDne detail={detail} onClose={() => setDetail(null)} isDark={isDark}
                    onVzit={vzitFlek} beruId={beruId} chybaAkce={chybaAkce}
                    onVratit={otevriVratit} onProhodit={otevriProhodit}
                    onZadost={(d) => setZadost({ datum: d })} />)}

            {seznamVolnych && (
                <VolneSmenySheet isDark={isDark} rok={rok} mesic={mesic}
                    onClose={() => setSeznamVolnych(false)} onZmena={obnov} />
            )}
            {zadost && (
                <ZadostOVolnoSheet datum={zadost.datum} isDark={isDark}
                    onClose={() => setZadost(null)} onSuccess={obnov} />
            )}
            {vratitSmenu && (
                <VratitSmenuModal smena={vratitSmenu} isDark={isDark}
                    onClose={() => setVratitSmenu(null)}
                    onSuccess={() => { setVratitSmenu(null); obnov(); }} />
            )}
            {prohoditSmenu && (
                <ProhoditSmenuModal smena={prohoditSmenu} isDark={isDark}
                    onClose={() => setProhoditSmenu(null)}
                    onSuccess={() => { setProhoditSmenu(null); obnov(); }} />
            )}
        </div>
    );
}

const sklonujVolne = (n) => (n === 1 ? 'volná' : (n >= 2 && n <= 4 ? 'volné' : 'volných'));

/** Volné fleky dne — řádek kreslí sdílený `VolnyFlek`, stejně jako celý seznam. */
function VolneDne({ volne, onVzit, beruId, isDark, kompakt = false }) {
    if (!volne?.length) return null;
    return volne.map(v => (
        <VolnyFlek key={v.id} flek={v} onVzit={onVzit} beru={beruId === v.id} isDark={isDark} kompakt={kompakt} />
    ));
}

/**
 * Vrátit / prohodit moji směnu. Tlačítko se nekreslí, když na něj už není čas —
 * nabídnout akci, kterou backend odmítne, je horší než ji nenabídnout.
 */
function AkceSmeny({ datum, smena, onVratit, onProhodit, isDark, kompakt = false }) {
    const vratit = lzeVratit(datum, smena);
    const prohodit = lzeProhodit(datum, smena);
    if (!vratit && !prohodit) return null;

    const styl = (barva) => ({
        flex: 1, height: kompakt ? 30 : 40, borderRadius: kompakt ? 7 : 10,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        border: `1px solid ${barva}55`, background: `${barva}14`, color: barva,
        fontSize: kompakt ? 11.5 : 13, fontWeight: 700, cursor: 'pointer',
    });

    return (
        <div style={{ display: 'flex', gap: kompakt ? 6 : 8 }}>
            {prohodit && (
                <button type="button" onClick={() => onProhodit({ ...smena, datum })} style={styl('#0CC0DF')}>
                    <ArrowLeftRight size={kompakt ? 12 : 14} /> Prohodit
                </button>
            )}
            {vratit && (
                <button type="button" onClick={() => onVratit({ ...smena, datum })} style={styl('#D85A30')}>
                    <RotateCcw size={kompakt ? 12 : 14} /> Vrátit
                </button>
            )}
        </div>
    );
}

/** Moje nepřítomnost na daný den — typ, stav, poznámka. */
function NepritomnostDne({ nedostupnost, isDark, kompakt = false }) {
    if (!nedostupnost) return null;
    const typ = TYP_NEPRITOMNOSTI[nedostupnost.typ] || TYP_NEPRITOMNOSTI.jine;
    const stav = STAV_NEPRITOMNOSTI[nedostupnost.stav] || { label: nedostupnost.stav, barva: '#6B7280' };
    const textMuted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: kompakt ? '7px 9px' : '10px 12px', borderRadius: kompakt ? 6 : 12,
            background: `${typ.barva}12`, border: `1px solid ${typ.barva}38`,
        }}>
            <CalendarOff size={kompakt ? 13 : 16} style={{ color: typ.barva, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: kompakt ? 12 : 13.5, fontWeight: 700, color: typ.barva }}>{typ.label}</div>
                <div style={{ fontSize: kompakt ? 10.5 : 11.5, color: textMuted }}>
                    {nedostupnost.poznamka || `${nedostupnost.od} – ${nedostupnost.do}`}
                </div>
            </div>
            <span style={{
                flexShrink: 0, fontSize: kompakt ? 9 : 9.5, fontWeight: 800,
                textTransform: 'uppercase', letterSpacing: 0.4, padding: '3px 8px', borderRadius: 999,
                color: stav.barva, background: `${stav.barva}1f`, border: `1px solid ${stav.barva}4d`,
            }}>{stav.label}</span>
        </div>
    );
}

/** Kolegové dne — manažer směny první, pak podle příjmení. */
const seraditKolegy = (kolegove) => [...(kolegove || [])].sort((a, b) => {
    if (a.manazer_smeny !== b.manazer_smeny) return a.manazer_smeny ? -1 : 1;
    return (a.prijmeni || '').localeCompare(b.prijmeni || '', 'cs');
});
const popisCizich = (n) => (n === 0
    ? 'Ten den nikdo nejede'
    : `Jede ${n} ${n === 1 ? 'člověk' : (n <= 4 ? 'lidi' : 'lidí')}`);
const popisKolegu = (n) => (n === 0
    ? 'Na směně jsi sám'
    : `${n} ${n === 1 ? 'kolega' : (n <= 4 ? 'kolegové' : 'kolegů')} na směně`);

/**
 * Detail dne na mobilu — standardní sheet (components/SheetModal): moje směny,
 * skutečnost odpracovaného dne a kolegové s tlačítkem Volat.
 */
function DetailDneSheet({ den, onClose, isDark, onVzit, beruId, chybaAkce, onVratit, onProhodit, onZadost }) {
    const smeny = den.smeny || [];
    const kolegove = seraditKolegy(den.kolegove);
    // Na dni, kde nejedu, jsou to cizí lidi — stejný seznam, jiný nadpis.
    const cizi = seraditKolegy(den.cizi);
    const lide = smeny.length ? kolegove : cizi;
    const volne = den.volne || [];
    const nedostupnost = den.nedostupnost || null;
    const skutecnost = den.skutecnost;
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const datumLabel = new Date(den.datum + 'T00:00:00').toLocaleDateString('cs-CZ', {
        weekday: 'long', day: 'numeric', month: 'long',
    });

    return (
        <SheetModal onClose={onClose} isDark={isDark}
            ikona={CalendarDays} barva="#0CC0DF"
            nadpis={datumLabel.charAt(0).toUpperCase() + datumLabel.slice(1)}
            podnadpis={smeny.length
                ? popisKolegu(kolegove.length)
                : (volne.some(v => v.muzu) ? 'Ten den nejedeš · je co vzít' : popisCizich(cizi.length))}
            paticka={!nedostupnost && (
                <button type="button" onClick={() => { onClose(); onZadost?.(den.datum); }} style={{
                    width: '100%', height: 44, borderRadius: 12,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    background: 'transparent', color: isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)',
                    border: `1px dashed ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'}`,
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>
                    <CalendarOff size={14} /> Požádat o volno na tento den
                </button>
            )}>
            {chybaAkce && (
                <div style={{
                    padding: '10px 12px', borderRadius: 12, fontSize: 12, lineHeight: 1.45,
                    background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444',
                }}>{chybaAkce}</div>
            )}

            {smeny.map(sm => (
                <SheetKarta key={sm.id} isDark={isDark} akcent={sm.sablon_barva || '#0CC0DF'}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: 14, fontWeight: 700, color: textPrimary,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{sm.sablon_nazev || 'Směna'}</div>
                        <div style={{ fontSize: 12, color: textMuted, fontVariantNumeric: 'tabular-nums', marginTop: 1 }}>
                            {sm.cas_od}–{sm.cas_do} · {fmtH(sm.hodin)}
                        </div>
                    </div>
                    {sm.stav === 'dokoncena' && (
                        <span style={{
                            fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4,
                            padding: '3px 8px', borderRadius: 999, flexShrink: 0,
                            color: '#10B981', background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.3)',
                        }}>Hotovo</span>
                    )}
                </SheetKarta>
            ))}

            {smeny.map(sm => (
                <AkceSmeny key={`akce-${sm.id}`} datum={den.datum} smena={sm}
                    onVratit={onVratit} onProhodit={onProhodit} isDark={isDark} />
            ))}

            <NepritomnostDne nedostupnost={nedostupnost} isDark={isDark} />

            {skutecnost && (
                <SheetKarta isDark={isDark} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px',
                    background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.22)',
                }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: textMuted, fontVariantNumeric: 'tabular-nums' }}>
                        <Clock size={13} /> {fmtH(skutecnost.hodiny)} odpracováno
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: '#10B981', fontVariantNumeric: 'tabular-nums' }}>
                        {/* Bez hodinové sazby je výdělek 0 — „0 Kč" by jen mátlo */}
                        {skutecnost.vydelek_kc > 0 && fmtKc(skutecnost.vydelek_kc)}
                        {skutecnost.dysko_kc !== 0 && (
                            <span style={{ fontSize: 12, fontWeight: 700, marginLeft: 6, color: skutecnost.dysko_kc >= 0 ? '#10B981' : '#EF4444' }}>
                                {fmtKcSigned(skutecnost.dysko_kc)}
                            </span>
                        )}
                    </span>
                </SheetKarta>
            )}

            {volne.length > 0 && <SheetNadpisSkupiny isDark={isDark}>Volné k rozebrání</SheetNadpisSkupiny>}
            <VolneDne volne={volne} onVzit={onVzit} beruId={beruId} isDark={isDark} />

            {lide.length > 0 && (
                <SheetNadpisSkupiny isDark={isDark}>
                    {smeny.length ? 'Na směně se mnou' : 'Kdo ten den jede'}
                </SheetNadpisSkupiny>
            )}
            {lide.map((k, idx) => (
                <SheetKarta key={`${k.zamestnanec_id ?? idx}-${idx}`} isDark={isDark}
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar clen={k} size={40} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                                fontSize: 14, fontWeight: 700, color: textPrimary,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>{k.jmeno} {k.prijmeni}</span>
                            {k.manazer_smeny && (
                                <span title="Manažer směny" style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                                    padding: '2px 6px', borderRadius: 999,
                                    fontSize: 9, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
                                    background: 'rgba(255,138,0,0.18)', color: '#FF8A00', border: '1px solid rgba(255,138,0,0.35)',
                                }}><Crown size={9} /> Manažer</span>
                            )}
                        </div>
                        <div style={{
                            fontSize: 11.5, color: textMuted, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                            {k.pozice_nazev ? `${k.pozice_nazev} · ` : ''}{k.cas_od}–{k.cas_do}
                        </div>
                    </div>
                    {k.telefon && (
                        <a href={`tel:${k.telefon}`} aria-label={`Volat ${k.jmeno}`} style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            height: 36, padding: '0 12px', borderRadius: 9, flexShrink: 0, textDecoration: 'none',
                            fontSize: 12, fontWeight: 700,
                            background: isDark ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.08)',
                            border: '1px solid rgba(16,185,129,0.40)', color: '#10B981',
                        }}>
                            <Phone size={13} /> Volat
                        </a>
                    )}
                </SheetKarta>
            ))}
        </SheetModal>
    );
}

/**
 * Detail vybraného dne (desktop) — portal do body (fixed), aby ho neořízla
 * karta cockpitu. Stejný princip jako hover tooltip dovedností v Přehledu.
 */
function DetailDne({ detail, onClose, isDark, onVzit, beruId, chybaAkce, onVratit, onProhodit, onZadost }) {
    const { den, rect } = detail;
    const smeny = den.smeny || [];
    const kolegove = seraditKolegy(den.kolegove);
    const cizi = seraditKolegy(den.cizi);
    const lide = smeny.length ? kolegove : cizi;
    const volne = den.volne || [];
    const nedostupnost = den.nedostupnost || null;
    const skutecnost = den.skutecnost;

    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const hranice = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const slotBg = isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-elevated)';

    const datumLabel = new Date(den.datum + 'T00:00:00').toLocaleDateString('cs-CZ', {
        weekday: 'long', day: 'numeric', month: 'long',
    });

    return createPortal((
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 99998 }}>
            <div onClick={e => e.stopPropagation()} style={{
                position: 'fixed', zIndex: 99999,
                width: 280, maxHeight: '60dvh', overflowY: 'auto', overscrollBehavior: 'contain',
                left: Math.min(Math.max(8, rect.left + rect.width / 2 - 140), window.innerWidth - 288),
                ...(rect.bottom + 260 > window.innerHeight
                    ? { bottom: window.innerHeight - rect.top + 6 }
                    : { top: rect.bottom + 6 }),
                ...MODAL_SKLO, padding: 14,
                display: 'flex', flexDirection: 'column', gap: 10,
            }}>
                {/* Hlavička */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: textPrimary, lineHeight: 1.25 }}>
                            {datumLabel}
                        </div>
                        <div style={{ fontSize: 11, color: textMuted }}>
                            {smeny.length ? popisKolegu(kolegove.length) : popisCizich(cizi.length)}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Zavřít" style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: textMuted, padding: 0,
                    }}>
                        <X size={16} />
                    </button>
                </div>

                {/* Moje směny toho dne */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {smeny.map(s => (
                        <div key={s.id} style={{
                            background: slotBg, border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)'}`,
                            borderLeft: `3px solid ${s.sablon_barva || '#0CC0DF'}`,
                            borderRadius: 6, padding: '7px 9px',
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    fontSize: 12, fontWeight: 600, color: textPrimary,
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>{s.sablon_nazev || 'Směna'}</div>
                                <div style={{ fontSize: 11, color: labelMuted, fontVariantNumeric: 'tabular-nums' }}>
                                    {s.cas_od}–{s.cas_do} · {fmtH(s.hodin)}
                                </div>
                            </div>
                            {s.stav === 'dokoncena' && (
                                <span style={{
                                    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                                    padding: '2px 6px', borderRadius: 999,
                                    color: '#10B981', background: 'rgba(16,185,129,0.14)',
                                    border: '1px solid rgba(16,185,129,0.3)', flexShrink: 0,
                                }}>Hotovo</span>
                            )}
                        </div>
                    ))}
                </div>

                {/* Vrátit / prohodit — jen dokud na to je čas */}
                {smeny.map(sm => (
                    <AkceSmeny key={`akce-${sm.id}`} datum={den.datum} smena={sm}
                        onVratit={onVratit} onProhodit={onProhodit} isDark={isDark} kompakt />
                ))}

                <NepritomnostDne nedostupnost={nedostupnost} isDark={isDark} kompakt />

                {/* Volné fleky toho dne */}
                {volne.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                            textTransform: 'uppercase', color: textMuted,
                        }}>Volné k rozebrání</span>
                        <VolneDne volne={volne} onVzit={onVzit} beruId={beruId} isDark={isDark} kompakt />
                    </div>
                )}

                {chybaAkce && (
                    <div style={{
                        padding: '7px 9px', borderRadius: 6, fontSize: 11, lineHeight: 1.4,
                        background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444',
                    }}>{chybaAkce}</div>
                )}

                {/* Skutečnost odpracovaného dne */}
                {skutecnost && (
                    <div style={{
                        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8,
                        padding: '7px 9px', borderRadius: 6,
                        background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
                    }}>
                        <span style={{ fontSize: 11, color: labelMuted, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtH(skutecnost.hodiny)} odpracováno
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: '#10B981', fontVariantNumeric: 'tabular-nums' }}>
                            {/* Bez hodinové sazby je výdělek 0 — „0 Kč" by jen mátlo */}
                            {skutecnost.vydelek_kc > 0 && fmtKc(skutecnost.vydelek_kc)}
                            {skutecnost.dysko_kc !== 0 && (
                                <span style={{ fontSize: 10.5, fontWeight: 700, marginLeft: 5, color: skutecnost.dysko_kc >= 0 ? '#10B981' : '#EF4444' }}>
                                    {fmtKcSigned(skutecnost.dysko_kc)}
                                </span>
                            )}
                        </span>
                    </div>
                )}

                {/* Kdo ten den jede — moji kolegové, nebo cizí směna */}
                {lide.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                            textTransform: 'uppercase', color: textMuted,
                        }}>{smeny.length ? 'Na směně se mnou' : 'Kdo ten den jede'}</span>
                        {lide.map((k, idx) => (
                            <div key={k.zamestnanec_id ?? idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Avatar clen={k} size={28} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <span style={{
                                            fontSize: 12, fontWeight: 600, color: textPrimary,
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>{k.jmeno} {k.prijmeni}</span>
                                        {k.manazer_smeny && (
                                            <Crown size={10} style={{ color: '#FF8A00', flexShrink: 0 }} aria-label="Manažer směny" />
                                        )}
                                    </div>
                                    <div style={{
                                        fontSize: 10.5, color: textMuted, fontVariantNumeric: 'tabular-nums',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        {k.pozice_nazev ? `${k.pozice_nazev} · ` : ''}{k.cas_od}–{k.cas_do}
                                    </div>
                                </div>
                                {k.telefon && (
                                    <a href={`tel:${k.telefon}`} title={k.telefon} style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                                        background: 'rgba(12,192,223,0.14)', color: '#0CC0DF',
                                        border: '1px solid rgba(12,192,223,0.25)',
                                    }}>
                                        <Phone size={12} />
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {!nedostupnost && (
                    <button type="button" onClick={() => { onClose(); onZadost?.(den.datum); }} style={{
                        height: 32, borderRadius: 8, marginTop: 2,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        background: 'transparent', color: labelMuted,
                        border: `1px dashed ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'}`,
                        fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                    }}>
                        <CalendarOff size={12} /> Požádat o volno
                    </button>
                )}
            </div>
        </div>
    ), document.body);
}
